import { Button, DatePicker, Typography, Row, Col, Checkbox, Switch, Tag, Select } from "antd";
import "../../Styles/styles-Reports.css";
import moment from "moment";
import { useSelector } from "react-redux";
import { useAuth0 } from "@auth0/auth0-react";
import { get_metric_info_from_identifier } from "../../Utils/chart_utils";
import { IGeneralReportsConfig, PLAYER_REPORT_DATA_STREAMS, REPORT_TYPES } from "./constants";
import './css/GeneralReportConfig.css'

const { Text } = Typography;
const { Option } = Select;

export const GeneralReportConfig = ({
    selectedReportTypeKey,
    getSelectReportTypeObj,
    config,
    setConfig,
    setMetricSelectorModalIsOpen,
    setMetricSelectorPrefix,
    setUseAggs
}: {
    selectedReportTypeKey?: string,
    getSelectReportTypeObj: (key?: string) => any,
    config: IGeneralReportsConfig,
    setConfig: React.Dispatch<React.SetStateAction<IGeneralReportsConfig>>,
    setMetricSelectorModalIsOpen: React.Dispatch<React.SetStateAction<boolean>>,
    setMetricSelectorPrefix: React.Dispatch<React.SetStateAction<string | undefined>>,
    setUseAggs: React.Dispatch<React.SetStateAction<boolean>>,
}) => {
    const {
        all_metric_types: allMetricTypes,
        positions,
        playerGroups,
        allPlayers,
        misc: { comparison_types },
        info: {
            coach_list: coach_list,
        },
    } = useSelector(({ team }) => team);

    const dateFormat = "YYYY-MM-DD";


    const { user } = useAuth0();

    const getOrderedCoachEmailList = () => {
        let res = [{
            label: (<span>{user?.name} <span style={{ fontSize: 12 }}>({user?.email})</span></span>),
            value: user?.email,
            key: user?.email
        }];
        coach_list?.forEach((coach: any) => {
            if (coach.email === user?.email) return;
            res.push({
                label: (<span>{coach.first_name} {coach.last_name} <span style={{ fontSize: 12 }}>({coach.email})</span></span>),
                value: coach.email,
                key: coach.email
            })
        })
        return res;
    }

    const handleGroupByPositionChange = (value: any) => {
        if (value === true) {
            setConfig({ ...config, group_by_position: true });
        } else {
            if (Object.keys(config).includes('group_by_position')) {
                delete (config as any).group_by_position;
                setConfig({ ...config });
            }
        }
    }

    const handleSingleDatePickerChange = (date: any, dateString: any) => {
        setConfig({ ...config, date: dateString });
    }

    const handleDateRangePickerChange = (date: any, dateStrings: any) => {
        setConfig({ ...config, date_range: dateStrings });
    }

    const handleEmailSelectionChange = (checkedEmails: any) => {
        setConfig({ ...config, emails: checkedEmails });
    }

    const handlePlayerReportDataStreamChange = (checkedValues: any) => {
        setConfig({ ...config, player_report_data_streams: checkedValues });
    }

    const handlePositionFilterChange = (value: any) => {
        setConfig({ ...config, position_filter: value });
    }

    const handlePlayerGroupFilterChange = (value: any) => {
        setConfig({ ...config, player_group_filter: value });
    }

    const handleSinglePlayerFilterChange = (value: any) => {
        setConfig({ ...config, player_filter: value });
    }

    const handleComparisonTypeChange = (value: any) => {
        setConfig({ ...config, comparison_type: value });
    }

    const handleCurrentModalType = (value: string) => {
        setConfig({ ...config, current_modal_type: value })
    }

    const getFormattedMetricSelection = (prefix: any) => {
        const metric_list: { agg: 'best' | 'avg' | 'sum', metric: string }[] = (config as any)[`${prefix}-metrics`] || [];

        let expected_total_count = 0;
        if (prefix === 'report-card') {
            expected_total_count = 20;
        } else if (prefix === 'gps') {
            expected_total_count = 4;
        } else {
            expected_total_count = REPORT_TYPES.find(x => x.key === prefix)?.metric_selector_info?.count || 0;
        }

        let names: any[] = [];

        metric_list.forEach((metric_tuple) => {
            const { metric_id: unique_identifier, metric_stat } = get_metric_info_from_identifier(metric_tuple.metric);

            const metric_type = unique_identifier.split('-')[0];
            const metric_obj = allMetricTypes?.[metric_type]?.find((x: any) => x.unique_identifier === unique_identifier);

            let cleaned_name = undefined;

            if (metric_tuple.agg === 'avg') {
                cleaned_name = "[A] "
            } else if (metric_tuple.agg === 'best') {
                cleaned_name = "[B] "
            } else {
                cleaned_name = "[C] "
            }

            if (metric_obj) {
                if (metric_obj.name.includes("Left")) {
                    const asymName = metric_obj.name.split(" Left ")

                    cleaned_name += `[Asym] ${asymName[0]}${asymName[1] ? ` ${asymName[1]}` : ''}`
                } else {
                    cleaned_name += metric_obj.name;
                }
            } else {
                cleaned_name += unique_identifier;
            }

            if (cleaned_name) {
                names.push(cleaned_name);
            }
        })

        return (
            <>
                <Text style={{ marginRight: '5px' }}>
                    ({names.length}/{expected_total_count})
                </Text>
                {names.map((name, index) => (
                    <Tag style={{ borderRadius: '4px', marginRight: 2 }} key={index}>{name}</Tag>
                ))}
            </>
        )
    }

    return (
        <div
            style={{
                backgroundColor: '#fafafa',
                padding: '10px',
                borderRadius: '8px',
                marginTop: '10px',
                display: selectedReportTypeKey ? 'flex' : 'none',
                flexDirection: 'column',
                gap: '10px',
            }}
        >
            {getSelectReportTypeObj(selectedReportTypeKey)?.requirements?.includes('single_player_filter') ? (
                <div className="general-report-position-filter">
                    <Text> Please select a player for the report:</Text>
                    <Select
                        showSearch
                        optionFilterProp="label"
                        allowClear
                        placeholder="Select Player"
                        onChange={handleSinglePlayerFilterChange}
                        value={config.player_filter}
                        style={{ width: '200px' }}
                        options={
                            allPlayers.filter((e: any) => e.retired === false).map((player: any) => ({
                                label: `${player.first_name} ${player.last_name} #${player.number}`,
                                value: player.player_id,
                            }))
                        }
                        filterOption={(input, option) => {
                            const label = option?.label;
                            return typeof label === 'string' && label.toLowerCase()?.includes(input?.toLowerCase())
                        }}
                    />
                </div>
            ) : null}


            {getSelectReportTypeObj(selectedReportTypeKey)?.requirements?.includes('date') ? (
                <div>
                    <Text> Please pick a date for the report:</Text>
                    <DatePicker
                        style={{ borderRadius: '8px', marginLeft: '10px' }}
                        onChange={handleSingleDatePickerChange}
                        value={config.date ? moment(config.date, dateFormat) : null}
                        format={dateFormat}
                        placeholder="Select Date"
                        allowClear
                    />
                </div>
            ) : null}

            {getSelectReportTypeObj(selectedReportTypeKey)?.requirements?.includes('date_range') ? (
                <div>
                    <Text>Please pick a date range for the report:</Text>
                    <DatePicker.RangePicker
                        style={{ borderRadius: '8px', marginLeft: '10px' }}
                        onChange={handleDateRangePickerChange}
                        value={config.date_range ? [config.date_range[0] ? moment(config.date_range[0], dateFormat) : null, config.date_range[1] ? moment(config.date_range[1], dateFormat) : null]: null}
                        format={dateFormat}
                        allowClear={false} />
                    {selectedReportTypeKey === 'single_week_distance_breakdown' && <Text type="secondary" style={{ marginLeft: '5px' }}>(Max 7 days)</Text>}
                    {selectedReportTypeKey === 'multi_week_distance_breakdown' && <Text type="secondary" style={{ marginLeft: '5px' }}>(Max 7 weeks)</Text>}
                    {selectedReportTypeKey === 'status_report' && <Text type="secondary" style={{ marginLeft: '5px' }}>(Max 12 weeks)</Text>}
                    {selectedReportTypeKey === 'weekly_body_weight_report' && <Text type="secondary" style={{ marginLeft: '5px' }}>(Max 12 weeks)</Text>}
                </div>
            ) : null}

            {getSelectReportTypeObj(selectedReportTypeKey)?.requirements?.includes('comparison') ? (
                <div className="general-report-position-filter">
                    <Text> Please select a type of comparison :</Text>
                    <Select
                        showSearch
                        optionFilterProp="value"
                        allowClear
                        onChange={handleComparisonTypeChange}
                        value={config.comparison_type || 'latest_vs_prev'}
                        options={comparison_types}
                        filterOption={(input, option) => {
                            const label = option?.label;
                            return typeof label === 'string' && label.toLowerCase()?.includes(input?.toLowerCase())
                        }}
                        style={{ width: '250px' }}
                    />
                </div>
            ) : null}

            {getSelectReportTypeObj(selectedReportTypeKey)?.requirements?.includes('position_filter') ? (
                <div className="general-report-position-filter">
                    <Text> Please select the positions to be included in the report (Optional):</Text>
                    <Select
                        showSearch
                        optionFilterProp="value"
                        allowClear
                        placeholder="All Positions"
                        mode="multiple"
                        onChange={handlePositionFilterChange}
                        value={config.position_filter || []}
                    >
                        {positions.map((pos: any, i: any) => (
                            <Option key={i} value={pos}>
                                {pos}
                            </Option>
                        ))}
                    </Select>
                </div>
            ) : null}

            {getSelectReportTypeObj(selectedReportTypeKey)?.requirements?.includes('player_group_filter') ? (
                <div className="general-report-position-filter">
                    <Text> Please select the player groups to be included in the report (Optional):</Text>
                    <Select
                        showSearch
                        optionFilterProp="value"
                        allowClear
                        placeholder="All Players"
                        mode="multiple"
                        onChange={handlePlayerGroupFilterChange}
                        value={config.player_group_filter || []}
                        options={
                            playerGroups.map((pl_group: any) => ({
                                label: pl_group.name,
                                value: pl_group.player_group_id,
                            }))
                        }
                        filterOption={(input, option) => {
                            const label = option?.label;
                            return typeof label === 'string' && label.toLowerCase()?.includes(input?.toLowerCase())
                        }}
                    />
                </div>
            ) : null}


            {getSelectReportTypeObj(selectedReportTypeKey)?.requirements?.includes('group_by_position') ? (
                <div>
                    <Text> Group by Position:</Text>

                    <Switch
                        checkedChildren="Yes"
                        unCheckedChildren="No"
                        checked={config.group_by_position || false}
                        onChange={handleGroupByPositionChange}
                        style={{ marginLeft: '10px' }}
                    />
                </div>
            ) : null}


            {getSelectReportTypeObj(selectedReportTypeKey)?.requirements?.includes('player_report_type_selector') ? (
                <div>
                    <Text> Select data streams to include in the report:</Text><br />

                    <Checkbox.Group
                        value={config.player_report_data_streams || []}
                        onChange={handlePlayerReportDataStreamChange}
                    >
                        <Col >
                            {PLAYER_REPORT_DATA_STREAMS.map((item, idx) => (
                                <Row key={`${item.key}-${idx}`}>
                                    <Checkbox value={item.value} style={{ marginLeft: '10px' }}>
                                        {item.label}
                                        {
                                            config?.player_report_data_streams?.includes(item.key) ? (
                                                <>
                                                    <div style={{ padding: '2px', margin: '5px 0', borderRadius: '4px', border: '1px solid #ccc', marginTop: '5px' }}>
                                                        {getFormattedMetricSelection(item.key)}
                                                        <Button
                                                            size="small"
                                                            type="link"
                                                            onClick={() => {
                                                                setMetricSelectorPrefix(item.key);
                                                                handleCurrentModalType(item.key)

                                                                const playerReport = REPORT_TYPES.find(x => x.key === 'player_report');
                                                                if (playerReport?.metric_selector_info) {
                                                                    playerReport.metric_selector_info.count = item.count;
                                                                    playerReport.metric_selector_info.types = item.types;
                                                                }

                                                                if (item.key === 'report-card') {
                                                                    setUseAggs(true);
                                                                } else {
                                                                    setUseAggs(false);
                                                                }
                                                                setMetricSelectorModalIsOpen(true)
                                                            }
                                                            }
                                                        >
                                                            <u>Click here to set metrics</u>
                                                        </Button>
                                                    </div>
                                                </>) : null
                                        }

                                    </Checkbox>
                                </Row>
                            ))}
                        </Col>
                    </Checkbox.Group>


                </div>
            ) : null}

            {getSelectReportTypeObj(selectedReportTypeKey)?.requirements?.includes('metric_selector') ? (
                <div>
                    <Text>Metrics to include in the report: </Text>
                    {getFormattedMetricSelection(getSelectReportTypeObj(selectedReportTypeKey).key)}
                    <Button
                        size="small"
                        type="link"
                        onClick={() => {
                            setMetricSelectorPrefix(getSelectReportTypeObj(selectedReportTypeKey).key);
                            setMetricSelectorModalIsOpen(true)
                            handleCurrentModalType(getSelectReportTypeObj(selectedReportTypeKey).key)
                        }
                        }
                    >
                        <u>Click here to configure metrics</u>
                    </Button>

                </div>
            ) : null}


            {getSelectReportTypeObj(selectedReportTypeKey)?.requirements?.includes('emails') ? (
                <div>
                    <Text>Select email recipients for report: </Text> <br />
                    <Checkbox.Group
                        value={config.emails || []}
                        onChange={handleEmailSelectionChange}
                    >
                        <Col style={{ marginTop: "0.5em", overflowY: "auto", maxHeight: "300px" }}>
                            {getOrderedCoachEmailList().map((email, idx) => (
                                <Row key={`${email}-${idx}`}>
                                    <Checkbox key={email.key} value={email.value} style={{ marginLeft: '10px' }}>
                                        {email.label}
                                    </Checkbox>
                                </Row>
                            ))}
                        </Col>
                    </Checkbox.Group>
                </div>
            ) : null}

        </div>
    )
}

export default GeneralReportConfig